import { Component, computed, effect, inject, OnInit, signal, untracked, viewChild, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { EpicrisisObject, WorkGridItemObject } from '@it2go/types';
import { isFolderStatusAllowed } from '../../../../../../../../types/src/services/enum/folder-status.enum';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Gridster, GridsterItem } from 'angular-gridster2';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { PersonBasicInfoComponent } from '../../../components/person/person-basic-info/person-basic-info.component';
import { MenuItem, MenuItemCommandEvent } from '@openng/optimus-ui/api';
import {
    TerminateFolderModalComponent,
} from '../../../components/person/terminate-folder-modal/terminate-folder-modal.component';
import {
    basicGridsterItem,
    DashboardGridsterItem,
    gridsterOptions,
    Safe,
    SCALING_FACTOR,
    smallBasicGridsterItem,
    tightBasicGridsterItem,
} from '../../../../shared/constants/dashboard-settings';
import { WorkDataServiceRecordObject } from '@libs/types/src/services/object-type/work/work-service-record.object';
import { WorkTypeEnum } from '@libs/types/src/common/enum/work-type.enum';
import {
    PersonPalliativeCareComponent,
} from '../../../components/person/person-palliative-care/person-palliative-care.component';
import {
    PersonShareDataWithServiceComponent,
} from '../../../components/person/person-share-data-with-service/person-share-data-with-service.component';
import { FolderNoteComponent } from '../../../components/person/folder-note/folder-note.component';
import { PersonRisksComponent } from '../../../components/person/person-risks/person-risks.component';
import { FolderDashboardPageStore } from './folder-dashboard-page.store';
import { isAllowed, routeParams, router } from '../../../../shared/shared.global-service';
import { folderUri, voucherDetailUri, workUri } from '../../../../../utils/router.utils';
import {
    FOLDER_DASHBOARD_BASIC_INFO_PROPERTIES_KEY,
    FOLDER_DASHBOARD_WIDGET_SETTINGS_KEY,
} from '@libs/types/src/services/types/folder-dashboard-settings.type';
import { FolderDashboardWidgetsEnum } from '@libs/types/src/services/enum/folder-dashboard-widgets.enum';
import {
    FolderValidationErrorObject,
} from '@libs/types/src/services/object-type/folder/folder-validation-error.object';
import {
    ProcedureValidatorErrorEnum,
} from '../../../../../../../../services/src/domain/service/procedure-validation/types/procedure-validator.error-enum';
import {
    RestoreFolderModalComponent,
} from '../../../components/person/restore-folder-modal/restore-folder-modal.component';
import { ConfirmBuilder } from '../../../../shared/service/confirm.builder';
import { contentGlobalSuccess } from '../../../content.error';
import { prepareDataToSave, prepareWidgetDataToView } from '../../../../../utils/dashboard.utils';
import {
    FOLDER_DASHBOARD_WIDGET_BILLING_STATUS,
    FOLDER_DASHBOARD_WIDGET_ANAMNESIS,
    FOLDER_DASHBOARD_WIDGET_EPICRISIS,
    FOLDER_DASHBOARD_WIDGET_ERRORS,
    FOLDER_DASHBOARD_WIDGET_IMPORTANT_DATA,
    FOLDER_DASHBOARD_WIDGET_LAST_CONTINUOUS_RECORD,
    FOLDER_DASHBOARD_WIDGET_MEDICATION,
    FOLDER_DASHBOARD_WIDGET_NOTE,
    FOLDER_DASHBOARD_WIDGET_PLANS,
    FOLDER_DASHBOARD_WIDGET_PROTOCOLS,
    FOLDER_DASHBOARD_WIDGET_RISKS,
    FOLDER_DASHBOARD_WIDGET_SERVICE_RECORD,
    FOLDER_DASHBOARD_WIDGET_STATE_HISTORY,
} from '../../../../../components/social-service/setting/service-setting-dashboard-page/service-setting-dashboard-page.component';
import {
    FolderContinuousRecordWidgetComponent,
} from '../../../../../components/social-service/folder/folder-continuous-record-widget/folder-continuous-record-widget.component';
import {
    SERVICE_SETTINGS_CONTINUOUS_RECORD_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { SpeedDial } from '@openng/optimus-ui/speeddial';
import { ButtonComponent } from '../../../../../components/kit/button/button.component';
import { WidgetComponent } from '../../../components/person/widget/widget.component';
import {
    PersonCurrentMedicationComponent,
} from '../../../components/person/person-current-medication/person-current-medication.component';
import {
    PersonMeasuredValuesComponent,
} from '../../../components/person/person-measured-values/person-measured-values.component';
import {
    PersonVisitRecordsComponent,
} from '../../../components/person/person-visit-records/person-visit-records.component';
import { PersonErrorComponent } from '../../../components/person/person-error/person-error.component';
import {
    IShownProperties,
    shownPropertiesDefaultValues,
} from '../../../components/person/person-basic-info/person-basic-info.types';
import { workTypeAllowedExpertises } from '@libs/types/src/services/enum/work-type.enum';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';
import {
    FolderImportantInfoWidgetComponent,
} from '../../../../../components/social-service/folder/folder-important-info-widget/folder-important-info-widget.component';
import { featureOn } from '../../../../../feature-flag';
import {
    FolderStatusHistoryWidgetComponent,
} from '../../../../../components/social-service/folder/folder-status-history-widget/folder-status-history-widget.component';
import {
    FolderBillingStatusWidgetComponent,
} from '../../../../../components/social-service/folder/folder-billing-status-widget/folder-billing-status-widget.component';
import {
    FolderProtocolsWidgetComponent,
} from '../../../../../components/social-service/folder/folder-protocols-widget/folder-protocols-widget.component';
import { SafeHtmlPipe } from '../../../../shared/pipes/safe-html.pipe';
import {
    FolderPlansWidgetComponent,
} from '../../../../../components/social-service/folder/folder-plans-widget/folder-plans-widget.component';
import {
    FolderPersonsHistoryDialogComponent,
} from '../../../../../components/social-service/folder/folder-persons/folder-persons-history-dialog/folder-persons-history-dialog.component';
import {
    FolderPersonDialogComponent,
} from '../../../../../components/social-service/folder/folder-persons/folder-person-dialog/folder-person-dialog.component';
import {
    FolderAnamnesisWidgetComponent,
} from '../../../../../components/medical-service/folder/folder-anamnesis-widget/folder-anamnesis-widget.component';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { BackgroundTaskLaunchService } from '../../../../layout/store/background-tasks/background-task-launch.service';
import { BackgroundActionType } from '@libs/types/src/notifications/types/background-action.types';
import { parseBackgroundTaskParams } from '../../../../layout/store/background-tasks/background-task.utils';

@Component({
    selector: 'app-folder-dashboard-page',
    templateUrl: './folder-dashboard-page.component.html',
    styleUrls: ['./folder-dashboard-page.component.scss'],
    providers: [FolderDashboardPageStore],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SpeedDial,
        ButtonComponent,
        Gridster,
        GridsterItem,
        PersonBasicInfoComponent,
        FolderContinuousRecordWidgetComponent,
        TranslatePipe,
        TerminateFolderModalComponent,
        WidgetComponent,
        PersonCurrentMedicationComponent,
        FolderNoteComponent,
        PersonRisksComponent,
        PersonPalliativeCareComponent,
        PersonMeasuredValuesComponent,
        PersonVisitRecordsComponent,
        PersonErrorComponent,
        PersonShareDataWithServiceComponent,
        FolderImportantInfoWidgetComponent,
        FolderStatusHistoryWidgetComponent,
        FolderBillingStatusWidgetComponent,
        FolderProtocolsWidgetComponent,
        SafeHtmlPipe,
        FolderPlansWidgetComponent,
        FolderAnamnesisWidgetComponent,
        Tooltip,
    ],
})
export class FolderDashboardPageComponent extends SubscriptionComponent implements OnInit {

    public readonly confirm = inject(ConfirmBuilder);
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(FolderDashboardPageStore);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly backgroundTaskLauncher = inject(BackgroundTaskLaunchService);
    protected readonly dialogService = inject(BaseDialogService);
    private folderExportSubmitting = false;

    @ViewChild(TerminateFolderModalComponent) terminateFolderModal!: TerminateFolderModalComponent;
    @ViewChild(PersonPalliativeCareComponent) palliativeCareComponent!: PersonPalliativeCareComponent;
    @ViewChild(PersonShareDataWithServiceComponent) personShareDataWithServiceComponent!: PersonShareDataWithServiceComponent;
    @ViewChild(FolderNoteComponent) folderNoteComponent!: FolderNoteComponent;
    @ViewChild(PersonRisksComponent) personRisksComponent!: PersonRisksComponent;
    public lastContinuousRecordComponent = viewChild<FolderContinuousRecordWidgetComponent>(FolderContinuousRecordWidgetComponent);
    protected readonly visitRecordsComponent = viewChild(PersonVisitRecordsComponent);

    protected serviceRecords: WorkGridItemObject<WorkDataServiceRecordObject>[] = [];

    hamburgerItems: MenuItem[] = [];
    hamburgerButtonSeverity = 'primary';

    options: Safe = gridsterOptions;

    personInfo: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.PERSON_INFO,
        init: false,
        cols: 6 * SCALING_FACTOR,
        rows: 6 * SCALING_FACTOR,
        x: 0,
        y: 0,
        minItemCols: 4 * SCALING_FACTOR,
        minItemRows: 3 * SCALING_FACTOR,
        dragEnabled: false,
        resizableHandles: {
            se: true,
            sw: false,
            ne: false,
            nw: false,
            e: true,
            s: true,
            w: false,
            n: false,
        },
    };

    importantInfo: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.IMPORTANT_INFO,
        ...tightBasicGridsterItem,
    };
    history: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.HISTORY,
        ...smallBasicGridsterItem,
    };
    serviceRecord: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.SERVICE_RECORD,
        ...basicGridsterItem,
    };
    epicrisis: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.EPICRISIS,
        ...basicGridsterItem,
        hasEdit: true,
    };
    anamnesis: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.ANAMNESIS,
        ...basicGridsterItem,
        hasEdit: true,
    };
    personNote: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.PERSON_NOTE,
        ...smallBasicGridsterItem,
        hasEdit: true,
    };
    lastContinuousRecord: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.LAST_CONTINUOUS_RECORD,
        ...smallBasicGridsterItem,
        hasEdit: true,
    };
    billingStatus: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.BILLING_STATUS,
        ...smallBasicGridsterItem,
        hasEdit: true,
    };
    protocols: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.PROTOCOLS,
        ...basicGridsterItem,
        hasEdit: true,
    };
    plans: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.PLANS,
        ...basicGridsterItem,
        hasEdit: true,
    };
    errorsInFolder: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.ERRORS_IN_FOLDER,
        ...basicGridsterItem,
        cols: 6 * SCALING_FACTOR,
    };
    riskyClient: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.RISKY_CLIENT,
        hasEdit: true,
        hasOpen: true,
        hasAdd: true,
        ...smallBasicGridsterItem,
    };
    shareDataWithService: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.SHARE_DATA_WITH_SERVICE,
        hasAdd: true,
        ...basicGridsterItem,
        cols: 6 * SCALING_FACTOR,
    };
    palliativeCare: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.PALLIATIVE_CARE,
        hasAdd: true,
        ...basicGridsterItem,
        cols: 7 * SCALING_FACTOR,
    };
    currentMedication: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.CURRENT_MEDICATION,
        ...basicGridsterItem,
    };
    visitRecords: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.VISIT_RECORDS,
        ...basicGridsterItem,
        cols: 14 * SCALING_FACTOR,
        rows: 6 * SCALING_FACTOR,
    };
    measuredValues: DashboardGridsterItem = {
        name: FolderDashboardWidgetsEnum.MEASURED_VALUES,
        ...basicGridsterItem,
        cols: 13 * SCALING_FACTOR,
    };

    items: Record<string, DashboardGridsterItem> = {};

    baseItems: Record<string, DashboardGridsterItem> = {
        personInfo: this.personInfo,
        importantInfo: this.importantInfo,
        history: this.history,
        serviceRecord: this.serviceRecord,
        personNote: this.personNote,
        errorsInFolder: this.errorsInFolder,
        riskyClient: this.riskyClient,
        shareDataWithService: this.shareDataWithService,
        currentMedication: this.currentMedication,
        visitRecords: this.visitRecords,
    };

    basicInfoProperties = shownPropertiesDefaultValues;

    protected showItems = false;

    private expertiseCode = '';

    private itemChangeSignal = signal<any>(null);

    protected readonly canEditAnamnesis = computed(() =>
        !this.folderStore.isTerminated()
        && (isAllowed['folderRecord.create']() || isAllowed['folderRecord.update']()),
    );
    protected readonly canEditEpicrisis = computed(() =>
        !this.folderStore.isTerminated()
        && (isAllowed['folderRecord.create']() || isAllowed['folderRecord.update']()),
    );

    constructor() {
        super();
        effect(() => {
            this.store.api.getFolderValidationErrors(FilterBuilder.singlePage({
                folderId: routeParams.folderId(),
                silent: false,
                sort: { column: 'date', direction: 'DESC' },
            }));
        });

        effect(async () => {
            const service = this.store.currentService();
            const userId = this.store.currentUserId;

            if (service && userId) {
                await this.store.api.getDashboardSettings(FilterBuilder.singlePage({
                    serviceId: service.id,
                    userId,
                }));

                this.expertiseCode = service?.expertise?.code || '';
                this.rebuildItems();

                this.hamburgerItems = this.generateHamburgerItems(this.expertiseCode);

                const basicInfoProperties = this.store.basicInfoProperties();
                if (basicInfoProperties) {
                    // todo - bug when user make change in this settings and then in profile settings, old settings will be loaded
                    this.basicInfoProperties = basicInfoProperties;
                } else {
                    // TODO - ohak kvuli multiple loading service z global store (nejprve se to cely naloaduje pro predchozi vybranou service a potom znovu pro aktualni)
                    this.basicInfoProperties = shownPropertiesDefaultValues;
                }

                this.showItems = true;
            }
        });

        // karty dalších osob ve složce přibývají/ubývají za běhu
        effect(() => {
            const cards = this.store.secondaryPersonCards();
            if (!this.showItems) return;

            untracked(() => {
                const known = Object.keys(this.items)
                    .filter((key) => key.startsWith(`${FolderDashboardWidgetsEnum.PERSON_INFO}_`));
                const expected = cards.map((card) => this.personCardKey(card.person.id));

                if (known.length !== expected.length || expected.some((key) => !known.includes(key))) {
                    this.rebuildItems();
                }
            });
        });

        effect(() => {
            const event = this.itemChangeSignal();
            if (event) {
                untracked(() => this.handleItemChanged(event));
            }
        });
    }

    public async ngOnInit(): Promise<void> {
        const workServiceRecords = await this.store.api.getWorks(FilterBuilder.singleItem({
            workType: WorkTypeEnum.SERVICE_RECORD,
            folderId: routeParams.folderId(),
            sort: { column: 'dateFrom', direction: 'DESC' },
        }));

        this.serviceRecords = workServiceRecords?.['items'] || [];
    }

    basicInfoPropertiesChanged(event: any) {
        this.saveDashboardSettings(event, FOLDER_DASHBOARD_BASIC_INFO_PROPERTIES_KEY);
    }

    /**
     * Sestaví dlaždice podle druhu služby, nastavení a osob ve složce.
     * Pozice rozpracované v této session mají přednost před těmi načtenými z DB.
     */
    private rebuildItems(): void {
        const currentPositions = Object.keys(this.items).length
            ? prepareDataToSave(this.items, FOLDER_DASHBOARD_WIDGET_SETTINGS_KEY)
            : {};

        this.items = { ...this.baseItems };

        const allowed = isFolderStatusAllowed(this.expertiseCode);

        if (allowed.epicrisisAllowed) {
            this.items['epicrisis'] = this.epicrisis;
        }
        if (allowed.anamnesisAllowed) {
            this.items['anamnesis'] = this.anamnesis;
        }
        if ('925' === this.expertiseCode) {
            this.items['palliativeCare'] = this.palliativeCare;
        }
        if (['902', '913', '925', '926'].includes(this.expertiseCode)) {
            this.items['measuredValues'] = this.measuredValues;
        }
        if (this.store.isSocial()) {
            const continuousRecordSettings = this.store.getServiceSettings(SERVICE_SETTINGS_CONTINUOUS_RECORD_TYPE_KEY);

            if (continuousRecordSettings.useCard ?? true) {
                this.items['lastContinuousRecord'] = {
                    ...this.lastContinuousRecord,
                    name: continuousRecordSettings.customCardName || this.lastContinuousRecord.name,
                };
            }
        }
        if (this.store.isSocial() && featureOn.SOCIAL_FOLDER_DASHBOARD) {
            this.items['billingStatus'] = this.billingStatus;
            this.items['protocols'] = this.protocols;
            this.items['plans'] = this.plans;
        }

        // každá další osoba ve složce má vlastní kartu; `name` musí odpovídat klíči kvůli handleItemChanged()
        for (const card of this.store.secondaryPersonCards()) {
            const key = this.personCardKey(card.person.id);
            this.items[key] = {
                ...this.personInfo,
                name: key,
                dragEnabled: true,
            };
        }

        this.items = prepareWidgetDataToView(
            this.items,
            { ...(this.store.widgetSettings() || {}), ...currentPositions },
            { personInfo: { x: 0, y: 0 } },
        );
    }

    protected personCardKey(personId: number): string {
        return `${FolderDashboardWidgetsEnum.PERSON_INFO}_${personId}`;
    }

    protected async openAnamnesis(): Promise<void> {
        await this.router.navigateByUrl(folderUri(
            routeParams.folderId(),
            this.store.currentService()?.id,
            'anamnesis',
        ), {
            state: { goBackAfterSave: true },
        });
    }

    protected async openEpicrisis(): Promise<void> {
        await this.router.navigateByUrl(folderUri(
            routeParams.folderId(),
            this.store.currentService()?.id,
            'epicrisis',
        ), {
            state: { goBackAfterSave: true },
        });
    }

    itemChanged(event: any) {
        // ensures that user made the change
        if (
            event?.itemComponent?.drag?.lastMouse.clientX ||
            event?.itemComponent?.drag?.lastMouse.clientY ||
            event?.itemComponent?.resize?.lastMouse.clientX ||
            event?.itemComponent?.resize?.lastMouse.clientY
        ) {
            this.itemChangeSignal.set(event);
        }
    }

    handleItemChanged(event: any) {
        const { item } = event;

        this.saveDashboardSettings(this.items, FOLDER_DASHBOARD_WIDGET_SETTINGS_KEY);

        this.items[item.name] = item as unknown as DashboardGridsterItem;
    }

    currentEpicrisis(epicrisis: EpicrisisObject[]) {
        return [...epicrisis]
            .sort((a, b) => new Date(a.createdAt) > new Date(b.createdAt) ? -1 : 1)
            ?.[0]?.data || '';
    }

    currentServiceRecord(records: WorkGridItemObject<WorkDataServiceRecordObject>[]) {
        return [...records]
            .sort((a, b) => new Date(a.dateFrom) > new Date(b.dateFrom) ? -1 : 1)
            ?.[0].workData?.note || '';
    }

    private generateHamburgerItems(expertiseCode: string): MenuItem[] {
        if (this.store.isTerminated()) {
            return [
                {
                    tooltipOptions: {
                        tooltipLabel: this.translate.instant('folder.restore.header'),
                        tooltipPosition: 'bottom',
                    },
                    icon: 'pi pi-lock-open',
                    command: () => {
                        this.dialogService.open(RestoreFolderModalComponent, {
                            data: { folderId: Number(routeParams.folderId()) },
                            header: this.translate.instant('folder.restore.header'),
                        });
                    },
                },
            ];
        }

        const allowedItems: MenuItem[] = [];

        if (this.store.multiPersonEnabled()) {
            if (this.store.isAllowed('folder.update')()) {
                allowedItems.push({
                    tooltipOptions: {
                        tooltipLabel: this.translate.instant('folder.persons.actions.add'),
                        tooltipPosition: 'bottom',
                    },
                    icon: 'pi pi-user-plus',
                    command: () => {
                        // v dialogu jde vybrat existující osoba, novou zakládá až plný formulář na routě add-person
                        this.dialogService.open(FolderPersonDialogComponent, {
                            header: this.translate.instant('folder.persons.actions.add'),
                            styleClass: 'p-dialog-width-40',
                            data: {
                                folderId: Number(routeParams.folderId()),
                                serviceId: this.store.currentService()?.id ?? null,
                                excludePersonIds: this.store.presentFolderPersons().map((it) => it.personId),
                            },
                        }).onClose.subscribe((added) => added && this.store.loadFolderPersons());
                    },
                });
            }

            allowedItems.push({
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('folder.persons.history'),
                    tooltipPosition: 'bottom',
                },
                icon: 'pi pi-history',
                command: () => {
                    // v dialogu jdou pobyty i editovat, karty osob se proto po zavření načtou znovu
                    this.dialogService.open(FolderPersonsHistoryDialogComponent, {
                        header: this.translate.instant('folder.persons.history'),
                        styleClass: 'p-dialog-width-60',
                        data: { folderId: Number(routeParams.folderId()) },
                    }).onClose.subscribe(() => this.store.loadFolderPersons());
                },
            });
        }

        if (this.store.isAllowed('work.create')()) {
            const allowed = isFolderStatusAllowed(expertiseCode);
            if (allowed.visitsAllowed) {
                allowedItems.push(
                    {
                        tooltipOptions: {
                            tooltipLabel: this.translate.instant('person.dashboard.hamburger.nurseVisit'),
                            tooltipPosition: 'bottom',
                        },
                        icon: 'pi pi-eye',
                        command: () => {
                            this.router.navigateByUrl(workUri({ workType: WorkTypeEnum.VISIT }, this.store.currentService()?.id), {
                                state: {
                                    serviceId: this.store.currentService()?.id,
                                    personId: this.store.folder()?.person?.id,
                                },
                            });
                        },
                    },
                    {
                        tooltipOptions: {
                            tooltipLabel: this.translate.instant('person.dashboard.hamburger.unrealisedVisit'),
                            tooltipPosition: 'bottom',
                        },
                        icon: 'pi pi-eye-slash',
                        command: () => {
                            this.router.navigateByUrl(workUri({ workType: WorkTypeEnum.UNREALIZED_VISIT }, this.store.currentService()?.id), {
                                state: {
                                    serviceId: this.store.currentService()?.id,
                                    personId: this.store.folder()?.person?.id,
                                },
                            });
                        },
                    },
                );
            }
            if (workTypeAllowedExpertises.interventions.includes(expertiseCode)) {
                allowedItems.push(
                    {
                        tooltipOptions: {
                            tooltipLabel: this.translate.instant('person.dashboard.hamburger.intervention'),
                            tooltipPosition: 'bottom',
                        },
                        icon: 'pi pi-pencil',
                        command: () => {
                            this.router.navigateByUrl(workUri({ workType: WorkTypeEnum.INTERVENTION }, this.store.currentService()?.id), {
                                state: {
                                    serviceId: this.store.currentService()?.id,
                                    personId: this.store.folder()?.person?.id,
                                },
                            });
                        },
                    },
                );
            }
        }
        if (isAllowed['operationalWork.create']()) {
            allowedItems.push(
                {
                    tooltipOptions: {
                        tooltipLabel: this.translate.instant('person.dashboard.hamburger.serviceRecord'),
                        tooltipPosition: 'bottom',
                    },
                    icon: 'pi pi-pencil',
                    command: () => {
                        this.router.navigateByUrl(workUri({ workType: WorkTypeEnum.SERVICE_RECORD }, this.store.currentService()?.id), {
                            state: {
                                serviceId: this.store.currentService()?.id,
                                personId: this.store.folder()?.person?.id,
                            },
                        });
                    },
                },
            );
        }
        if (isAllowed['doctorWork.create']()) {
            if (workTypeAllowedExpertises.doctorRecords.includes(expertiseCode)) {
                allowedItems.push(
                    {
                        tooltipOptions: {
                            tooltipLabel: this.translate.instant('person.dashboard.hamburger.doctorRecord'),
                            tooltipPosition: 'bottom',
                        },
                        icon: 'pi pi-plus',
                        command: () => {
                            this.router.navigateByUrl(workUri({ workType: WorkTypeEnum.DOCTOR_RECORD }, this.store.currentService()?.id), {
                                state: {
                                    serviceId: this.store.currentService()?.id,
                                    personId: this.store.folder()?.person?.id,
                                },
                            });
                        },
                    },
                );
            }
        }
        if (isAllowed['specialistWork.create']()) {
            if (workTypeAllowedExpertises.specialistRecords.includes(expertiseCode)) {
                allowedItems.push(
                    {
                        tooltipOptions: {
                            tooltipLabel: this.translate.instant('person.dashboard.hamburger.expertRecord'),
                            tooltipPosition: 'bottom',
                        },
                        icon: 'pi pi-pencil',
                        command: () => {
                            this.router.navigateByUrl(workUri({ workType: WorkTypeEnum.SPECIALIST_RECORD }, this.store.currentService()?.id), {
                                state: {
                                    serviceId: this.store.currentService()?.id,
                                    personId: this.store.folder()?.person?.id,
                                },
                            });
                        },
                    },
                );
            }
        }

        allowedItems.push(
            {
                tooltipOptions: {
                    tooltipLabel: this.translate.instant('person.dashboard.hamburger.exportFolder'),
                    tooltipPosition: 'bottom',
                },
                icon: 'pi pi-file-export',
                command: () => {
                    this.exportFolder();
                },
            },
        );

        if (this.store.isAllowed('folder.update')()) {
            allowedItems.push(
                // Temporary hidden
                // {
                //     tooltipOptions: {
                //         tooltipLabel: this.translate.instant('person.dashboard.hamburger.widgetToDashboard'),
                //         tooltipPosition: 'bottom',
                //     },
                //     icon: 'pi pi-th-large',
                //     command: () => {
                //         return;
                //     }, //TODO
                // },
                {
                    tooltipOptions: {
                        tooltipLabel: this.translate.instant('person.dashboard.hamburger.closeFolder'),
                        tooltipPosition: 'bottom',
                    },
                    icon: 'pi pi-ban',
                    command: () => {
                        this.terminateFolderModal?.show();
                    },
                },
                {
                    tooltipOptions: {
                        tooltipLabel: this.translate.instant('person.dashboard.hamburger.deleteFolder'),
                        tooltipPosition: 'bottom',
                    },
                    icon: 'pi pi-trash',
                    command: (event: MenuItemCommandEvent) => {
                        this.confirm.confirmDelete(
                            async () => {
                                await this.store.api.deleteFolder({ id: routeParams.folderId() });
                                contentGlobalSuccess.next('common.deleted');
                                await router.navigateByUrl(folderUri(null, this.store.currentService()?.id));
                            },
                            { event } as any,
                        );
                    },
                },
            );
        }

        return allowedItems;
    }

    protected async goToWork(error: FolderValidationErrorObject): Promise<void> {
        if (error.workId) {
            const work = await this.store.api.getWorks(FilterBuilder.filter({ id: error.workId }));
            const item = work?.items?.[0];
            if (item) await router.navigateByUrl(workUri(item, error.serviceId));
        } else if (
            [
                ProcedureValidatorErrorEnum.MISSING_PROCEDURE_80090_80091 as string,
                ProcedureValidatorErrorEnum.MISSING_DOCTOR_RECORD as string,
            ].includes(error.error)
        ) {
            const workType = error.error === ProcedureValidatorErrorEnum.MISSING_PROCEDURE_80090_80091 ? 'visit' : 'doctor-record';
            await this.router.navigate(
                [workUri({ workType }, error.serviceId)],
                {
                    state: {
                        serviceId: this.store.currentService()?.id || null,
                        personId: this.store.folder()?.person?.id || null,
                        dateFrom: new Date(error.date),
                    },
                },
            );
        } else if (error.error === ProcedureValidatorErrorEnum.NEW_PROCEDURE_ON_VOUCHER as string) {
            await this.goToVoucher(error);
        }
    }

    protected async goToVoucher(error: FolderValidationErrorObject): Promise<void> {
        const { voucherId } = JSON.parse(error.data);
        if (!voucherId) return;
        await this.router.navigateByUrl(voucherDetailUri({ folderId: error.folderId, voucherId }, error.serviceId));
    }

    private saveDashboardSettings(data: any, key: string) {
        const serviceId = this.store.currentService()?.id;
        if (this.store.currentUserId && serviceId) {
            this.store.api.updateDashboardSettings({
                serviceId: serviceId,
                userId: this.store.currentUserId,
                data: JSON.stringify(prepareDataToSave(data, key)),
                key,
            }).then();
        }
    }

    private async exportFolder(): Promise<void> {
        if (this.folderExportSubmitting) return;
        const folderId = Number(routeParams.folderId());
        this.folderExportSubmitting = true;
        try {
            await this.backgroundTaskLauncher.start({
                type: BackgroundActionType.FOLDER_EXPORT,
                payload: { folderId },
                alreadyRunningMessage: 'person.folderExport.messages.alreadyRunning',
                failedMessage: 'person.folderExport.messages.startFailed',
                isSameTask: (task) =>
                    parseBackgroundTaskParams(task.params)?.folderId === folderId,
            });
        } finally {
            this.folderExportSubmitting = false;
        }
    }

    protected readonly FOLDER_DASHBOARD_WIDGET_SERVICE_RECORD = FOLDER_DASHBOARD_WIDGET_SERVICE_RECORD;
    protected readonly FOLDER_DASHBOARD_WIDGET_ANAMNESIS = FOLDER_DASHBOARD_WIDGET_ANAMNESIS;
    protected readonly FOLDER_DASHBOARD_WIDGET_MEDICATION = FOLDER_DASHBOARD_WIDGET_MEDICATION;
    protected readonly FOLDER_DASHBOARD_WIDGET_IMPORTANT_DATA = FOLDER_DASHBOARD_WIDGET_IMPORTANT_DATA;
    protected readonly FOLDER_DASHBOARD_WIDGET_EPICRISIS = FOLDER_DASHBOARD_WIDGET_EPICRISIS;
    protected readonly FOLDER_DASHBOARD_WIDGET_STATE_HISTORY = FOLDER_DASHBOARD_WIDGET_STATE_HISTORY;
    protected readonly FOLDER_DASHBOARD_WIDGET_NOTE = FOLDER_DASHBOARD_WIDGET_NOTE;
    protected readonly FOLDER_DASHBOARD_WIDGET_RISKS = FOLDER_DASHBOARD_WIDGET_RISKS;
    protected readonly FOLDER_DASHBOARD_WIDGET_ERRORS = FOLDER_DASHBOARD_WIDGET_ERRORS;
    protected readonly FOLDER_DASHBOARD_WIDGET_LAST_CONTINUOUS_RECORD = FOLDER_DASHBOARD_WIDGET_LAST_CONTINUOUS_RECORD;
    protected readonly routeParams = routeParams;
    protected readonly featureOn = featureOn;
    protected readonly FOLDER_DASHBOARD_WIDGET_BILLING_STATUS = FOLDER_DASHBOARD_WIDGET_BILLING_STATUS;
    protected readonly FOLDER_DASHBOARD_WIDGET_PROTOCOLS = FOLDER_DASHBOARD_WIDGET_PROTOCOLS;
    protected readonly FOLDER_DASHBOARD_WIDGET_PLANS = FOLDER_DASHBOARD_WIDGET_PLANS;
}
